import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Car, MapPin, Clock, Shield, TrendingUp, Users } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLanding = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user already has a session
    const checkSession = async () => {
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });
          if (response.ok) {
            navigate('/dashboard');
            return;
          }
        } catch (err) {
          console.error('Session check failed:', err);
        }
      }
      setLoading(false);
    };
    checkSession();
  }, [navigate]);

  const handleLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="hero-section">
      {/* Header */}
      <header className="relative z-10 py-4 px-6 lg:px-12 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
            alt="AutoGo Icon" 
            className="h-8 w-8 sm:h-12 sm:w-12"
          />
          <span className="text-xl sm:text-3xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          <Button
            onClick={() => navigate('/driver/login')}
            variant="outline"
            size="sm"
            className="border-gray-300 hover:bg-white/80 text-xs sm:text-sm px-2 sm:px-4"
          >
            <span className="hidden sm:inline">{t('driver_access')}</span>
            <span className="sm:hidden">Conductor</span>
          </Button>
          <Button
            onClick={() => navigate('/admin/login')}
            variant="outline"
            size="sm"
            className="border-gray-300 hover:bg-white/80 text-xs sm:text-sm px-2 sm:px-4"
            data-testid="admin-login-link"
          >
            <span className="hidden sm:inline">{t('admin_login')}</span>
            <span className="sm:hidden">Admin</span>
          </Button>
        </div>
      </header>

      {/* Hero Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
          {/* Left Column - Text */}
          <div className="space-y-6 sm:space-y-8 animate-fade-in">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" style={{color: '#0a3042'}}>
              {t('professional_car')}
              <span className="block mt-2" style={{color: '#36e961'}}>
                {t('transfer_service')}
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('landing_description')}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Button
                onClick={handleLogin}
                size="lg"
                className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full shadow-lg text-white"
                style={{backgroundColor: '#36e961'}}
                data-testid="get-started-btn"
              >
                {t('get_started')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto border-2 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full"
                style={{borderColor: '#0a3042', color: '#0a3042'}}
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('learn_more')}
              </Button>
            </div>
          </div>

          {/* Right Column - Feature Cards */}
          <div className="grid grid-cols-2 gap-6">
            <div className="glass-card p-6 rounded-2xl card-hover">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#e8f9ed'}}>
                <MapPin className="w-6 h-6" style={{color: '#36e961'}} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{t('realtime_tracking')}</h3>
              <p className="text-sm text-gray-600">{t('realtime_desc')}</p>
            </div>

            <div className="glass-card p-6 rounded-2xl card-hover mt-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#d4f1e0'}}>
                <Clock className="w-6 h-6" style={{color: '#0a3042'}} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{t('flexible_timing')}</h3>
              <p className="text-sm text-gray-600">{t('flexible_desc')}</p>
            </div>

            <div className="glass-card p-6 rounded-2xl card-hover">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#e8f9ed'}}>
                <Shield className="w-6 h-6" style={{color: '#36e961'}} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{t('secure_payment')}</h3>
              <p className="text-sm text-gray-600">{t('secure_desc')}</p>
            </div>

            <div className="glass-card p-6 rounded-2xl card-hover mt-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#d4f1e0'}}>
                <TrendingUp className="w-6 h-6" style={{color: '#0a3042'}} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{t('clear_pricing')}</h3>
              <p className="text-sm text-gray-600">{t('clear_desc')}</p>
            </div>
          </div>
        </div>
      </main>

      {/* Features Section */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('why_choose_us')}</h2>
          <p className="text-lg text-gray-600">{t('professional_service')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="glass-card p-8 rounded-2xl text-center card-hover">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#e8f9ed'}}>
              <Users className="w-8 h-8" style={{color: '#36e961'}} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('professional_drivers')}</h3>
            <p className="text-gray-600">{t('drivers_desc')}</p>
          </div>

          <div className="glass-card p-8 rounded-2xl text-center card-hover">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#d4f1e0'}}>
              <Shield className="w-8 h-8" style={{color: '#0a3042'}} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('fully_insured')}</h3>
            <p className="text-gray-600">{t('insured_desc')}</p>
          </div>

          <div className="glass-card p-8 rounded-2xl text-center card-hover">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#e8f9ed'}}>
              <TrendingUp className="w-8 h-8" style={{color: '#36e961'}} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('transparent_pricing')}</h3>
            <p className="text-gray-600">{t('transparent_desc')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientLanding;
